import types from '../mutation-types';

import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { buildDraftKey } from 'dashboard/helper/draftMessageHelper';

const EMPTY_ATTACHMENTS = [];

const state = {
  records: LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES) || {},
  // Aceleres: o texto do rascunho já sem assinatura, guardado só para o
  // preview da lista. `records` guarda o que volta para o editor e pode conter
  // a assinatura; usá-lo no preview marcaria como rascunho toda conversa que o
  // agente apenas abriu, porque a assinatura entra no editor sozinha.
  previews: LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_PREVIEWS) || {},
  // Aceleres: anexos ainda não enviados, por conversa. Só em memória — são
  // objetos File e data URLs de preview, que estouram a cota do localStorage.
  // Por isso o rascunho de anexo sobrevive à troca de conversa, mas não a um
  // recarregamento da página.
  attachments: {},
  replyEditorMode: REPLY_EDITOR_MODES.REPLY,
};

export const getters = {
  get: _state => key => {
    return _state.records[key] || '';
  },
  getAttachments: _state => conversationId => {
    return _state.attachments[conversationId] || EMPTY_ATTACHMENTS;
  },
  // A lista de conversas só mostra rascunho de resposta pública. O texto da
  // nota interna continua guardado na chave própria e volta ao editor, mas
  // não deve aparecer como se fosse uma mensagem para o contato.
  getDraftSummary: _state => conversationId => {
    const text = (
      _state.previews[buildDraftKey(conversationId, REPLY_EDITOR_MODES.REPLY)] ||
      ''
    ).trim();

    return {
      text,
      attachments: _state.attachments[conversationId] || EMPTY_ATTACHMENTS,
    };
  },
  // Aceleres: enquanto houver texto, nota ou anexo pendente, o rascunho
  // continua visível mesmo quando a conversa volta a ser aberta. Assim o
  // cartão permanece no bloco superior até o conteúdo ser enviado ou apagado,
  // sem descer novamente ao receber o foco.
  hasVisibleDraft: (_state, _getters) => conversationId => {
    if (!conversationId) return false;

    const { text, attachments } = _getters.getDraftSummary(conversationId);
    return Boolean(text.trim()) || attachments.length > 0;
  },
  getReplyEditorMode: _state => _state.replyEditorMode,
};

export const actions = {
  set: async ({ commit }, { key, message, preview = '' }) => {
    commit(types.SET_DRAFT_MESSAGES, { key, message, preview });
  },
  delete: ({ commit }, { key }) => {
    commit(types.REMOVE_DRAFT_MESSAGES, { key });
  },
  setAttachments: ({ commit }, { conversationId, attachments }) => {
    if (attachments && attachments.length) {
      commit(types.SET_DRAFT_ATTACHMENTS, { conversationId, attachments });
      return;
    }
    commit(types.REMOVE_DRAFT_ATTACHMENTS, { conversationId });
  },
  deleteAttachments: ({ commit }, { conversationId }) => {
    commit(types.REMOVE_DRAFT_ATTACHMENTS, { conversationId });
  },
  setReplyEditorMode: ({ commit }, { mode }) => {
    commit(types.SET_REPLY_EDITOR_MODE, { mode });
  },
};

// O preview anda junto do rascunho para os dois nunca divergirem: sempre que
// `records` é escrito ou apagado, `previews` acompanha na mesma mutação.
const writePreview = ($state, key, preview) => {
  const { [key]: previousPreview, ...remainingPreviews } =
    $state.previews || {};
  $state.previews = preview
    ? { ...remainingPreviews, [key]: preview }
    : remainingPreviews;
  LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_PREVIEWS, $state.previews);
};

export const mutations = {
  [types.SET_DRAFT_MESSAGES]($state, { key, message, preview = '' }) {
    $state.records = {
      ...$state.records,
      [key]: message,
    };
    LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES, $state.records);
    writePreview($state, key, preview);
  },
  [types.REMOVE_DRAFT_MESSAGES]($state, { key }) {
    const { [key]: draftToBeRemoved, ...updatedRecords } = $state.records;
    $state.records = updatedRecords;
    LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES, $state.records);
    writePreview($state, key, '');
  },
  [types.SET_DRAFT_ATTACHMENTS]($state, { conversationId, attachments }) {
    $state.attachments = {
      ...$state.attachments,
      [conversationId]: attachments,
    };
  },
  [types.REMOVE_DRAFT_ATTACHMENTS]($state, { conversationId }) {
    const { [conversationId]: removed, ...remaining } = $state.attachments;
    $state.attachments = remaining;
  },
  [types.SET_REPLY_EDITOR_MODE]($state, { mode }) {
    $state.replyEditorMode = mode;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
